import { Toast } from "primereact/toast";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Quotation, QuotationList } from "../../Controllers/Types/Quotation/QuotationType";
import { Dialog } from "primereact/dialog";
import { Ripple } from "primereact/ripple";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { CheckboxChangeEvent } from "primereact/checkbox";
import TextInput from "../../Components/TextInput";

interface QuotationReportConfigModalProps {
    isOpen: boolean;
    reportTitle: string;
    view: 'Simple' | 'Detail',
    onClose: () => void;
    toast: React.RefObject<Toast>;
    quotationNo: number | null;
    quotationDetail: QuotationList | null;
    quotation: Quotation|null
}

const QuotationReportConfigModal: React.FC<QuotationReportConfigModalProps> = (
    {
        isOpen,
        reportTitle,
        view,
        onClose,
        toast,
        quotationNo,
        quotationDetail,
        quotation
    }
) => {
    const navigate = useNavigate();
    const [isFullViewOptionChecked, setIsFullViewOptionChecked] = useState<boolean>(false);
    const [isMaterialOptionChecked, setIsMaterialOptionChecked] = useState<boolean>(false);
    const [isLabourOptionChecked, setIsLabourOptionChecked] = useState<boolean>(false);
    const [isTotalOptionChecked, setIsTotalOptionChecked] = useState<boolean>(false);

    const [headerContent, setHeaderContent] = useState<string>('');
    const [footerContent, setFooterContent] = useState<string>('');

    const [headerContentError, setHeaderContentError] = useState<string>('');
    const [footerContentError, setFooterContentError] = useState<string>('');

    const handlePreviewReport = () => {
        const reportViewOptions: string[] = [];

        if (isFullViewOptionChecked) reportViewOptions.push("Full");
        if (isMaterialOptionChecked) reportViewOptions.push("Material");
        if (isLabourOptionChecked) reportViewOptions.push("Labour");
        if (isTotalOptionChecked) reportViewOptions.push("Total");

        navigate(`/quotation/report`, {
            state: {
                quotationNo: quotationNo,
                quotationDetail: quotationDetail,
                reportType: view,
                reportView: reportViewOptions,
                headerContent: headerContent,
                footerContent: footerContent,
                quotation:quotation
            }
        });
    }

    const handleCloseModal = () => {
        onClose();
        handleResetFields();
        handleResetErrors();
    }

    const handleResetFields = () => {
        setIsFullViewOptionChecked(false);
        setIsMaterialOptionChecked(false);
        setIsLabourOptionChecked(false);
        setIsTotalOptionChecked(false);
    }

    const handleResetErrors = () => {
        setHeaderContentError('');
        setFooterContentError('');
    }

    const handleCheckOption = (
        e: CheckboxChangeEvent,
        option: 'All' | 'Material' | 'Labour' | 'Total'
    ) => {
        const checked = e.checked || false;

        if (option === 'All') {
            setIsFullViewOptionChecked(checked);
            setIsMaterialOptionChecked(checked);
            setIsLabourOptionChecked(checked);
            setIsTotalOptionChecked(checked);
            return;
        }

        const nextMaterial = option === 'Material' ? checked : isMaterialOptionChecked;
        const nextLabour = option === 'Labour' ? checked : isLabourOptionChecked;
        const nextTotal = option === 'Total' ? checked : isTotalOptionChecked;

        if (option === 'Material') setIsMaterialOptionChecked(checked);
        if (option === 'Labour') setIsLabourOptionChecked(checked);
        if (option === 'Total') setIsTotalOptionChecked(checked);

        const allChecked = nextMaterial && nextLabour && nextTotal;
        setIsFullViewOptionChecked(allChecked);
    };

    const modalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                Report configuration
            </h5>

            <button
                type="button"
                className="close_modal_btn p-ripple"
                onClick={onClose}
            >
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    const modalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Close"
                className="custom_btn secondary"
                onClick={handleCloseModal}
            />

            <Button
                label={'Preview'}
                onClick={handlePreviewReport}
                className="custom_btn primary"
                disabled={!(isFullViewOptionChecked || isMaterialOptionChecked || isLabourOptionChecked || isTotalOptionChecked)}
            />
        </div>
    );

    return (
        <Dialog
            visible={isOpen}
            header={modalHeader}
            footer={modalFooter}
            headerClassName="custom_modal_header"
            className="custom_modal_dialog modal_dialog_sm"
            onHide={onClose}
            draggable={false}
            dismissableMask
        >
            <div className="custom_modal_body">
                <div className="p-1 p-sm-3 pt-2 pt-sm-2 pb-sm-0 w-100">
                    <div className="row">
                        <div className="col-12">
                            <h6 className="data_view_head mb-2 mb-sm-3">
                                {reportTitle}
                            </h6>
                        </div>

                        <div className="col-12">
                            <div className="data_check_area">
                                <div className="data_check_group main">
                                    <label htmlFor="fullView" className="data_check_label">Full view (M+L)</label>
                                    <Checkbox
                                        inputId="fullView"
                                        onChange={(e) => handleCheckOption(e, 'All')}
                                        checked={isFullViewOptionChecked}
                                    />
                                </div>

                                <div className="data_check_group">
                                    <label htmlFor="material" className="data_check_label">Material</label>
                                    <Checkbox
                                        inputId="material"
                                        onChange={(e) => handleCheckOption(e, 'Material')}
                                        checked={isMaterialOptionChecked}
                                    />
                                </div>

                                <div className="data_check_group">
                                    <label htmlFor="labour" className="data_check_label">Labour</label>
                                    <Checkbox
                                        inputId="labour"
                                        onChange={(e) => handleCheckOption(e, 'Labour')}
                                        checked={isLabourOptionChecked}
                                    />
                                </div>

                                <div className="data_check_group">
                                    <label htmlFor="total" className="data_check_label">Total</label>
                                    <Checkbox
                                        inputId="total"
                                        onChange={(e) => handleCheckOption(e, 'Total')}
                                        checked={isTotalOptionChecked}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="col-12">
                            <TextInput
                                id="headerContent"
                                label="Header"
                                labelHtmlFor="headerContent"
                                required={false}
                                value={headerContent}
                                placeholder="Header content"
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setHeaderContent(e.target.value)}
                                error={headerContentError}
                            />
                        </div>

                        <div className="col-12">
                            <TextInput
                                id="footerContent"
                                label="Footer content"
                                labelHtmlFor="footerContent"
                                required={false}
                                value={footerContent}
                                placeholder="Footer content"
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFooterContent(e.target.value)}
                                error={footerContentError}
                                formGroupClassName="mb-sm-3 mb-2"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </Dialog>
    )
}

export default QuotationReportConfigModal;